import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, takeUntil, tap } from 'rxjs';

import { DcIcons } from '../../../ui/app-dc.icons';
import {
	ExpositionsSelector,
	activateExpositions,
	clearExpositionsData,
	deactivateExpositions,
	deleteExpositionAccess,
	deleteExpositionFromView,
	deleteExpositions,
	expositionActionsCompleted,
	publishExpositions,
	updateExpositionParams,
	updateExpositionsData,
	updateExpositionWithoutJobTracking,
} from '../../store';

export interface IUpdateParam {
	isChecked: boolean;
	isDisabled: boolean;
	tooltip?: string;
}

export enum ExpositionAction {
	Publish = 'publish',
	ClearData = 'clear-data',
	UpdateData = 'update-data',
	Update = 'update',
	Activate = 'activate',
	Deactivate = 'deactivate',
	Delete = 'delete',
	DeleteAccess = 'delete-access',
}

export interface IExpositionActionConfirmationModalData {
	expositionId: number | Array<number>;
	element: string;
	action: ExpositionAction;
	hasActiveAccess?: boolean;
	hasColumnsChanged?: boolean;
	accessId?: string;
	fromView?: boolean;
}
@Component({
	selector: 'app-exposition-action-confirmation-popup',
	templateUrl: './exposition-action-confirmation-popup.component.html',
	styleUrls: ['./exposition-action-confirmation-popup.component.scss'],
})
export class ExpositionActionConfirmationPopupComponent<
	T extends IExpositionActionConfirmationModalData
> extends DcBaseComponent {
	public readonly Icons = DcIcons;
	public vo$: Observable<{
		element: string;
		expositionId: number | Array<number>;
		action: ExpositionAction;
		isMultipleElement: boolean;
		isActionInProgress: boolean;
	}>;
	public updateMetadata = false;
	public updateColumns: IUpdateParam = {
		isChecked: false,
		isDisabled: !this.modalData.hasActiveAccess,
		tooltip: this.modalData.hasActiveAccess
			? $localize`:i18n=@@expositions.publication.api.confirmation.popup.columnsDisabled.tooltip:`
			: $localize`:i18n=@@expositions.publication.api.confirmation.popup.columnsDisabled.noActiveAccess.tooltip:`,
	};
	public updateAccess: IUpdateParam = {
		isChecked: false,
		isDisabled: !this.modalData.hasActiveAccess,
		tooltip: this.modalData.hasActiveAccess
			? $localize`:i18n=@@expositions.publication.api.confirmation.popup.accessDisabled.tooltip:`
			: $localize`:i18n=@@expositions.publication.api.confirmation.popup.accessDisabled.noActiveAccess.tooltip:`,
	};
	public confirmDeletion = false;

	protected readonly DcIcons = DcIcons;
	protected readonly ExpositionAction = ExpositionAction;
	public constructor(
		private readonly modalService: AppModalService,
		private readonly store: Store,
		private readonly expositionsSelector: ExpositionsSelector,
		private readonly actions$: Actions,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'exposition-confirm';

		this.vo$ = combineAll({
			element: this.modalData.element,
			isMultipleElement:
				Array.isArray(this.modalData.expositionId) &&
				this.modalData.element === '',
			expositionId: this.modalData.expositionId,
			action: this.modalData.action,
			isActionInProgress: this.expositionsSelector.getActionInProgress$(),
		});

		this.handleSubscriptions();
	}

	public changeUpdateColumns(value: boolean): void {
		this.updateColumns = {
			isChecked: value,
			isDisabled: false,
		};
		this.updateAccess = {
			isChecked: value,
			isDisabled: value,
			tooltip: $localize`:i18n=@@expositions.publication.api.confirmation.popup.accessDisabled.tooltip:`,
		};
	}
	public changeUpdateAccess(value: boolean): void {
		this.updateAccess = {
			isChecked: value,
			isDisabled: false,
		};

		if (this.modalData.hasColumnsChanged) {
			this.updateColumns = {
				isChecked: value,
				isDisabled: value,
				tooltip: $localize`:i18n=@@expositions.publication.api.confirmation.popup.columnsDisabled.tooltip:`,
			};
		}
	}

	public launchAction(): void {
		const expositionIds = this.expositionIds();
		switch (this.modalData.action) {
			case ExpositionAction.Publish:
				return this.store.dispatch(
					publishExpositions({
						expositionIds,
					})
				);
			case ExpositionAction.UpdateData:
				return this.store.dispatch(
					updateExpositionsData({
						expositionIds,
					})
				);
			case ExpositionAction.ClearData:
				return this.store.dispatch(
					clearExpositionsData({
						expositionIds,
					})
				);
			case ExpositionAction.Activate:
				return this.store.dispatch(
					activateExpositions({
						expositionIds,
					})
				);
			case ExpositionAction.Deactivate:
				return this.store.dispatch(
					deactivateExpositions({
						expositionIds,
					})
				);
			case ExpositionAction.Update:
				return this.launchUpdate(expositionIds[0]);
			case ExpositionAction.Delete:
				if (this.modalData.fromView) {
					return this.store.dispatch(
						deleteExpositionFromView({
							expositionId: expositionIds[0],
						})
					);
				}
				return this.store.dispatch(
					deleteExpositions({
						expositionIds,
					})
				);
			case ExpositionAction.DeleteAccess:
				return this.store.dispatch(
					deleteExpositionAccess({
						expositionId: expositionIds[0],
						accessId: this.modalData.accessId ?? '',
					})
				);
		}
	}

	private launchUpdate(expositionId: number): void {
		if (this.updateColumns.isChecked) {
			return this.store.dispatch(
				updateExpositionParams({
					expositionId,
					updateMetadata: this.updateMetadata,
					updateColumns: this.updateColumns.isChecked,
					updateAccess: this.updateAccess.isChecked,
				})
			);
		}
		return this.store.dispatch(
			updateExpositionWithoutJobTracking({
				expositionId,
				updateMetadata: this.updateMetadata,
				updateAccess: this.updateAccess.isChecked,
			})
		);
	}

	private expositionIds(): Array<number> {
		const { expositionId } = this.modalData;
		return Array.isArray(expositionId) ? expositionId : [expositionId];
	}

	private handleSubscriptions(): void {
		this.actions$
			.pipe(
				takeUntil(this.onDestroy$),
				ofType(expositionActionsCompleted),
				tap(() => this.modalService.closeActiveModal())
			)
			.subscribe();
	}
}
